import * as React from 'react';
import { useContext, useEffect } from 'react';
import { getFormattedDate } from '../utils/utilities';

type PageContextProviderProps = {
	children: React.ReactElement,
	page: string
}

interface IAction {
	type: pageContextActionTypes;
	payload: any;
}

interface IState {
	searchValue: string,
	secondarySearchValue: string,
	currentPage: string,
	pageData: any,
	chosenDate: any[],
	timezone: string,
	dateType: string,
	secondaryChosenDate: any[],
}

enum pageContextActionTypes {
	SEARCH_SAVE = 'SEARCH_SAVE',
	SECONDARY_SEARCH_SAVE = 'SECONDARY_SEARCH_SAVE',
	CURRENT_PAGE_SAVE = 'CURRENT_PAGE_SAVE',
	PAGE_DATA_SAVE = 'PAGE_DATA_SAVE',
	DATE_SAVE = 'DATE_SAVE',
	TIMEZONE_SAVE = 'TIMEZONE_SAVE',
	DATE_TYPE_SAVE = 'DATE_TYPE_SAVE',
	SECONDARY_DATE_SAVE = 'SECONDARY_DATE_SAVE',
}

const pageContextReducer = (state: IState, action: IAction): IState => {
	switch (action.type) {
		case pageContextActionTypes.SEARCH_SAVE: {
			return {
				...state,
				searchValue: action.payload
			};
		}
		case pageContextActionTypes.SECONDARY_SEARCH_SAVE: {
			return {
				...state,
				secondarySearchValue: action.payload
			};
		}
		case pageContextActionTypes.CURRENT_PAGE_SAVE: {
			return {
				...state,
				currentPage: action.payload
			};
		}
		case pageContextActionTypes.PAGE_DATA_SAVE: {
			return {
				...state,
				pageData: action.payload
			};
		}
		case pageContextActionTypes.DATE_SAVE: {
			return {
				...state,
				chosenDate: action.payload
			};
		}
		case pageContextActionTypes.SECONDARY_DATE_SAVE: {
			return {
				...state,
				secondaryChosenDate: action.payload
			};
		}
		case pageContextActionTypes.TIMEZONE_SAVE: {
			return {
				...state,
				timezone: action.payload
			};
		}
		case pageContextActionTypes.DATE_TYPE_SAVE: {
			return {
				...state,
				dateType: action.payload
			};
		}
		default: {
			return state;
		}
	}
};

const initialState = {
	searchValue: '',
	secondarySearchValue: '',
	currentPage: '',
	pageData: {},
	chosenDate: [],
	secondaryChosenDate: [],
	timezone: '',
	dateType: ''
};

const PageContextProvider = ({ children, page }: PageContextProviderProps): JSX.Element => {
	const [pageState, setPageState] = React.useReducer(pageContextReducer, initialState);

	const setSearchValue = (value): void => {
		setPageState({ type: pageContextActionTypes.SEARCH_SAVE, payload: value });
	};
	const setSecondarySearchValue = (value): void => {
		setPageState({ type: pageContextActionTypes.SECONDARY_SEARCH_SAVE, payload: value });
	};
	const saveCurrentPage = (value): void => {
		setPageState({ type: pageContextActionTypes.CURRENT_PAGE_SAVE, payload: value });
	};
	const setPageData = (value): void => {
		setPageState({ type: pageContextActionTypes.PAGE_DATA_SAVE, payload: value });
	};
	const setChosenDate = (value): void => {
		setPageState({ type: pageContextActionTypes.DATE_SAVE, payload: value });
	};
	const setSecondaryChosenDate = (value): void => {
		setPageState({ type: pageContextActionTypes.SECONDARY_DATE_SAVE, payload: value });
	};
	const setTimezone = (value): void => {
		setPageState({ type: pageContextActionTypes.TIMEZONE_SAVE, payload: value });
	};
	const setDateType = (value): void => {
		setPageState({ type: pageContextActionTypes.DATE_TYPE_SAVE, payload: value });
	};
	const value = {
		...pageState,
		setSearchValue,
		setPageData,
		setSecondarySearchValue,
		setChosenDate,
		setSecondaryChosenDate,
		setTimezone,
		setDateType
	};

	useEffect(() => {
		if (pageState.currentPage !== page) {
			setSearchValue('');
			setChosenDate([]);
			setSecondaryChosenDate([]);
			saveCurrentPage(page);
			setTimezone('');
			setDateType('');
		}
	}, [pageState.currentPage, page]);

	return (
		<PageContext.Provider value={value}>
			{children}
		</PageContext.Provider>
	);
};

interface IPageContext<T> {
	searchValue: string;
	secondarySearchValue: string;
	setSearchValue: (value: string) => void;
	setSecondarySearchValue: (value: string) => void;
	setPageData: (value: T) => void;
	pageData: T;
	chosenDate: any;
	setChosenDate: (value: any[]) => void;
	secondaryChosenDate: any;
	setSecondaryChosenDate: (value: any[]) => void;
	timezone: string,
	setTimezone: (value: string) => void;
	dateType: string,
	setDateType: (value: string) => void;
}

const PageContext = React.createContext<IPageContext<any>>({} as IPageContext<any>);

function usePageContext<T>(): IPageContext<T> {
	return useContext(PageContext);
}

export { PageContextProvider, usePageContext };
